/** TSS per ISO week (hours on hover); a bar opens that week's page. */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { FitnessPoint } from '../../types/api.ts';
import { weeklyLoad, type WeekLoad } from '../../lib/chartData.ts';
import { formatDayShort } from '../../lib/chartFormat.ts';
import { TooltipBox, TooltipRow } from './ChartParts.tsx';

export function WeeklyLoadChart({ points }: Readonly<{ points: FitnessPoint[] }>) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const weeks = useMemo(() => weeklyLoad(points), [points]);
  const weekLabel = (w: WeekLoad) =>
    t('charts.weekly.weekLabel', {
      week: formatDayShort(w.week, i18n.language),
      tss: w.tss,
      hours: w.hours,
    });

  return (
    <div className="flex flex-col gap-2">
      <div
        role="img"
        aria-label={t('charts.weekly.summary', { count: weeks.length })}
        style={{ width: '100%', height: 220 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={weeks}
            margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
            onClick={(state) => {
              const week = weeks[Number(state?.activeTooltipIndex)];
              if (week) navigate(`/weeks/${week.week}`);
            }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
            <XAxis
              dataKey="week"
              tickFormatter={(d: string) => formatDayShort(d, i18n.language)}
              stroke="var(--chart-axis)"
              fontSize={11}
              minTickGap={16}
            />
            <YAxis stroke="var(--chart-axis)" fontSize={11} width={40} />
            <Tooltip content={<WeekTooltip />} cursor={{ fill: 'var(--chart-grid)' }} />
            <Bar dataKey="tss" name="TSS" fill="var(--chart-ctl)" fillOpacity={0.7} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Keyboard and screen-reader route to the same week pages. */}
      <ul className="sr-only">
        {weeks.map((w) => (
          <li key={w.week}>
            <Link to={`/weeks/${w.week}`}>{weekLabel(w)}</Link>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-(--color-text-secondary)">{t('charts.weekly.hint')}</p>
    </div>
  );
}

function WeekTooltip({
  active,
  payload,
}: Readonly<{ active?: boolean; payload?: ReadonlyArray<{ payload?: WeekLoad }> }>) {
  const { t, i18n } = useTranslation();
  const w = payload?.[0]?.payload;
  if (!active || !w) return null;
  return (
    <TooltipBox title={t('charts.weekly.weekOf', { week: formatDayShort(w.week, i18n.language) })}>
      <TooltipRow label="TSS" value={`${w.tss}`} />
      <TooltipRow label={t('charts.weekly.hours')} value={t('charts.weekly.hoursValue', { n: w.hours })} />
    </TooltipBox>
  );
}
